/**
 * Recover from stale-chunk errors after a deploy.
 *
 * When the user has the SPA open across a deploy, the next dynamic import
 * (lazy route, code-split chunk, preloaded CSS) fails because the old hashed
 * asset filename no longer exists on the server. Vite's preload helper
 * surfaces this as one of:
 *   - "Unable to preload CSS for /assets/<hash>.css"
 *   - "Failed to fetch dynamically imported module: <url>"
 *   - "Importing a module script failed."
 *   - ChunkLoadError (legacy / webpack-like bundlers)
 *
 * Strategy: detect any of those, then reload the page once. A sessionStorage
 * flag prevents an infinite reload loop if reload doesn't actually fix it
 * (e.g. the new bundle has its own bug).
 */

const RELOAD_FLAG = "dmy:chunk-reload-attempted";

const CHUNK_ERROR_PATTERNS = [
  "Unable to preload CSS",
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  "ChunkLoadError",
  "error loading dynamically imported module",
];

export function isChunkError(err: unknown): boolean {
  if (!err) return false;
  const msg =
    err instanceof Error
      ? err.message
      : typeof err === "string"
        ? err
        : typeof err === "object" && err !== null && "message" in err
          ? String((err as { message: unknown }).message)
          : "";
  return CHUNK_ERROR_PATTERNS.some((p) => msg.includes(p));
}

function tryReloadOnce(): void {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode, etc.) — reload anyway.
  }
  window.location.reload();
}

export function installChunkErrorRecovery(): void {
  // Clear the flag once the new bundle has booted successfully — the next
  // deploy gets a fresh chance to reload.
  window.addEventListener("load", () => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      /* ignore */
    }
  });

  window.addEventListener("error", (event) => {
    if (isChunkError(event.error) || isChunkError(event.message)) {
      tryReloadOnce();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkError(event.reason)) {
      tryReloadOnce();
    }
  });
}
